'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShieldCheck, 
  ChevronUp, 
  Compass, 
  ArrowUp, 
  ArrowUpRight,
  Activity,
  Layers
} from 'lucide-react';

interface FloorItem {
  id: string;
  num: string;
  name: string;
  anchor: string;
  tag: string;
  range: [number, number];
}

const FLOORS: FloorItem[] = [
  { id: 'f1', num: '01', name: 'Калькулятор 7.5x', anchor: '#valuation-matrix', tag: 'EBITDA', range: [0, 14] },
  { id: 'f2', num: '02', name: 'Кейс Остоженки', anchor: '#case-study', tag: 'Разбор срыва', range: [14, 28] },
  { id: 'f3', num: '03', name: 'Санация актива', anchor: '#diagnostic', tag: 'QoE & Аудит', range: [28, 42] },
  { id: 'f4', num: '04', name: 'Стратегия выхода', anchor: '#exit-strategy', tag: 'Exit Map', range: [42, 56] },
  { id: 'f5', num: '05', name: 'Синдикат & Прецеденты', anchor: '#syndicate', tag: 'Инвесторы', range: [56, 70] },
  { id: 'f7', num: '06', name: 'Интеллектуальный капитал', anchor: '#vault-docs', tag: 'База M&A', range: [70, 85] },
  { id: 'f6', num: '07', name: 'Оценка бизнеса', anchor: '#gateway', tag: 'Zero-Trace', range: [85, 100] },
];

export function FloatingBottomDock() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeFloorIndex, setActiveFloorIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
          const currentScrollY = window.scrollY;

          // Track precise percentage
          if (totalHeight > 0) {
            const progress = Math.min(100, Math.max(0, Math.round((currentScrollY / totalHeight) * 100)));
            setScrollProgress(progress);

            // Determine active floor from progress
            const index = FLOORS.findIndex((f) => progress >= f.range[0] && progress <= f.range[1]);
            if (index !== -1) {
              setActiveFloorIndex(index);
            }
          }

          setHasScrolled(currentScrollY > 80);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = useCallback((hash: string) => {
    const target = document.querySelector(hash);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleOpenStressTest = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sp_open_vault_handshake', { detail: { step: 1 } }));
    }
  }, []);

  const activeFloor = FLOORS[activeFloorIndex] || FLOORS[0];

  return (
    <aside
      aria-label="Навигационная стеклянная панель и шкала чтения"
      className="fixed bottom-4 sm:bottom-6 inset-x-0 z-50 flex flex-col items-center pointer-events-none px-3 sm:px-4 select-none"
    >
      {/* Floor quick-selector popover (Frosted Champagne Glass) */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-auto mb-3 w-full max-w-2xl p-4 sm:p-5 rounded-[16px] bg-[#FAF8F5]/95 border border-[#8C6D37]/35 text-[#141416] backdrop-blur-2xl shadow-[0_20px_50px_-10px_rgba(20,20,22,0.18),_inset_0_1px_0_rgba(255,255,255,0.9)] overflow-hidden"
          >
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-[#8C6D37]/15 px-1">
              <div className="flex items-center gap-2 font-mono text-[10px] text-[#8C6D37] tracking-[0.16em] uppercase font-bold">
                <Compass className="w-3.5 h-3.5 text-[#8C6D37]" />
                <span>РЕЕСТР ЭТАЖЕЙ АРХИТЕКТУРЫ СДЕЛКИ</span>
              </div>
              <span className="font-mono text-[10px] text-[#8C6D37] bg-[#8C6D37]/10 px-2 py-0.5 rounded-full border border-[#8C6D37]/20 font-semibold">
                ДОСЬЕ: {scrollProgress}%
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {FLOORS.map((f, idx) => {
                const isActive = activeFloorIndex === idx;
                return (
                  <button
                    key={f.id}
                    onClick={() => {
                      scrollToSection(f.anchor);
                      setIsExpanded(false);
                    }}
                    className={`flex items-start gap-2.5 p-2.5 rounded-[10px] text-left transition-all duration-150 border cursor-pointer ${
                      isActive
                        ? 'border-[#8C6D37] bg-[#8C6D37]/15 text-[#141416] shadow-xs font-medium'
                        : 'border-[#141416]/[0.06] hover:border-[#8C6D37]/40 bg-white/60 hover:bg-white text-[#141416]'
                    }`}
                  >
                    <span className={`font-mono text-[11px] font-bold px-1.5 py-0.5 rounded-[4px] ${
                      isActive ? 'bg-[#8C6D37] text-white' : 'bg-[#8C6D37]/10 text-[#8C6D37]'
                    }`}>
                      {f.num}
                    </span>
                    <div className="flex flex-col min-w-0">
                      <span className={`text-[12px] font-medium leading-tight truncate ${idx === 6 ? 'animate-pulse text-[#8C6D37]' : 'text-[#141416]'}`}>{f.name}</span>
                      <span className="text-[9px] font-mono text-[#8C6D37] uppercase tracking-wider mt-0.5 font-semibold">{f.tag}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* =========================================================================
          MAIN FROSTED CHAMPAGNE LIQUID GLASS DOCK (Светлое шелковое стекло)
          ========================================================================= */}
      <div className="pointer-events-auto relative max-w-2xl w-full">
        {/* Glass Container with Warm Platinum/Champagne Trim & Deep Soft Shadow */}
        <div className="relative rounded-[16px] bg-[#FAF8F5]/90 text-[#141416] border border-[#8C6D37]/35 shadow-[0_16px_40px_-8px_rgba(20,20,22,0.14),_0_2px_8px_rgba(20,20,22,0.06),_inset_0_1px_0_rgba(255,255,255,0.95)] backdrop-blur-xl px-3 sm:px-4 py-2 flex items-center justify-between gap-2.5 sm:gap-3 overflow-hidden">
          
          {/* Subtle Top Light Edge */}
          <div className="absolute top-0 left-6 right-6 h-[1px] bg-gradient-to-r from-transparent via-white to-transparent pointer-events-none" />

          {/* =====================================================================
              1. LEFT: FLOOR SELECTOR BADGE
              ===================================================================== */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex items-center gap-2 px-2.5 py-1.5 rounded-[8px] bg-white/70 hover:bg-white border border-[#8C6D37]/25 hover:border-[#8C6D37]/60 transition-all duration-200 cursor-pointer shadow-2xs"
            title="Открыть навигатор этажей"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#8C6D37] transition-transform group-hover:scale-125" />
            <span className="font-mono text-[10px] text-[#334155] uppercase tracking-[0.14em] font-semibold">
              ЭТАЖ
            </span>
            <span className="font-mono text-[11px] text-[#8C6D37] font-bold">
              {activeFloor.num}
            </span>
            <ChevronUp 
              size={11} 
              className={`text-[#334155] transition-transform duration-200 ${isExpanded ? 'rotate-180 text-[#8C6D37]' : ''}`} 
            />
          </button>

          {/* Active section name indicator (Desktop) */}
          <div className="hidden md:flex items-center text-[11px] font-serif text-[#141416] tracking-normal border-l border-[#8C6D37]/20 pl-3 relative overflow-hidden">
            <span className={`truncate max-w-[130px] font-medium ${activeFloorIndex === 6 ? 'animate-pulse text-[#8C6D37]' : ''}`}>{activeFloor.name}</span>
          </div>

          {/* =====================================================================
              2. CENTER: PRECISION 6-SEGMENT PROGRESS GAUGE (ШКАЛА ПРОКРУТКИ)
              ===================================================================== */}
          <div className="flex items-center gap-2 px-2.5 py-1 bg-white/80 rounded-[8px] border border-[#8C6D37]/20 shadow-2xs">
            {/* 6 Precision Step Bars */}
            <div className="flex items-center gap-1">
              {FLOORS.map((_, i) => {
                const isPassed = activeFloorIndex >= i;
                const isCurrent = activeFloorIndex === i;
                return (
                  <div
                    key={i}
                    className={`h-[4px] rounded-[1px] transition-all duration-300 ${
                      isCurrent
                        ? 'w-4 bg-[#8C6D37] shadow-[0_0_6px_rgba(140,109,55,0.4)]'
                        : isPassed
                        ? 'w-2.5 bg-[#8C6D37]/70'
                        : 'w-2.5 bg-[#141416]/10'
                    }`}
                  />
                );
              })}
            </div>

            {/* Numeric Percentage */}
            <span className="font-mono text-[10px] font-bold text-[#8C6D37] pl-1">
              {scrollProgress}%
            </span>
          </div>

          {/* =====================================================================
              3. RIGHT: STRESS-TEST CTA BUTTON & SCROLL-TO-TOP
              ===================================================================== */}
          <div className="flex items-center gap-2">
            {/* Scroll-To-Top button (Discrete) */}
            {hasScrolled && (
              <button
                onClick={scrollToTop}
                aria-label="Наверх"
                className="w-7 h-7 rounded-[8px] bg-white/70 hover:bg-white border border-[#8C6D37]/20 hover:border-[#8C6D37]/50 flex items-center justify-center text-[#334155] hover:text-[#141416] transition-all cursor-pointer shadow-2xs"
                title="Наверх"
              >
                <ArrowUp size={11} />
              </button>
            )}

            {/* Primary Action Button: Embossed Paper / Letterpress Effect */}
            <button
              onClick={handleOpenStressTest}
              className="relative inline-flex items-center gap-1.5 sm:gap-2 px-4 py-1.5 sm:py-2 rounded-[6px] bg-[#F7F4EE] border border-[#DDD5C7] shadow-[inset_0_2px_4px_rgba(0,0,0,0.06),0_1px_0_rgba(255,255,255,1),0_4px_12px_rgba(0,0,0,0.03)] transition-all duration-200 active:scale-[0.98] cursor-pointer overflow-hidden flex-shrink-0"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/70 to-transparent -translate-x-full animate-[megaShimmer_4s_infinite]" />
              <ShieldCheck className="relative z-10 w-3.5 h-3.5 text-[#8C6D37] drop-shadow-[0_1px_0_rgba(255,255,255,1)]" />
              <span 
                className="relative z-10 whitespace-nowrap font-bold text-[#8C6D37] tracking-[0.15em] text-[10px] sm:text-[11px] font-mono uppercase"
                style={{ textShadow: '0 1px 1px rgba(255,255,255,1), 0 -1px 1px rgba(140,109,55,0.15)' }}
              >
                ОЦЕНКА БИЗНЕСА
              </span>
            </button>
          </div>

        </div>
      </div>
    </aside>
  );
}

export default FloatingBottomDock;
